import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { Employee, EmployeerStatus } from '../../../models/employee.model';
import { EmployeeService } from '../../../services/employee.service';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { SelectOption } from '../../../../shared/models/dropdowm.model';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-employee-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    DatePipe,
    TagStatusComponent,
  ],
  templateUrl: './employee-list.component.html',
})
export class EmployeeListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private employeeService = inject(EmployeeService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  employees = signal<Employee[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  employeeStatusOptions = this.employeeService.getEmployeerStatusOptions();

  fullNameFilter?: string;
  emailFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.employeeService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.fullNameFilter,
        this.emailFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.employees.set(page.items as Employee[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os colaboradores',
              life: 3000,
            });
          }
        },
      });
  }

  getStatusDescription(status: EmployeerStatus): string {
    const option = this.employeeStatusOptions.filter(
      (x: SelectOption) => x.value == EmployeerStatus[status]
    );
    return option.length > 0 ? option[0].label : '-';
  }
}
